<template>
  <div class="search-results">
    <AdvancedTable
      :headers="headers"
      :items="items"
      :rowsPerPage="3"
      :clickable="true"
      :title="title"
      id="gs-results"
      @cellClicked="onItemSelect"
      :noDataText="noDataText"
      :sortBy="sortBy"
    >
      <template #items="{ item }">
        <tr>
          <template v-for="(header, index) in visibleHeaders">
            <truncate-concat
              @clicked="onItemSelect(item)"
              v-if="header.truncate"
              :tabbable="true"
              :maxChars="header.maxChars"
              :key="index"
              :value="getCellValue(item, header)"
              class="clickable-result"
              tag="td"
            />
            <!-- View Details Link -->
            <td
              v-else-if="header.value === 'groupDetails'"
              :key="`grp_${index}`"
              class="clickable-result"
            >
              <a href="#" @click="onViewDetails(item)">View Details</a>
            </td>
            <td
              v-else-if="header.value === 'select'"
              @click="onItemSelect(item)"
              :key="`sel_${index}`"
              class="clickable-result select"
            >
              Select
            </td>
            <!-- normal cell, nothing special -->
            <td
              v-else
              @click="onItemSelect(item)"
              :key="`_${index}`"
              class="clickable-result"
            >
              {{ getCellValue(item, header) }}
            </td>
          </template>
        </tr>
      </template>
    </AdvancedTable>
  </div>
</template>

<script>
import { useRoute, useRouter, useStore } from '@vue3shims';
import AdvancedTable from '@organisms/AdvancedTable.vue';
import TruncateConcat from '@atoms/ConcatAndEllipseValues.vue';
import groupSelectorHeaders from './groupSelectorHeaders';
import { formatDate, isFunction } from '@utils';
import { computed, onBeforeMount, ref, watch } from '@vue/composition-api';
import { useGroupSelector } from '@composables/useGroupSelector';

export default {
  setup(props, { emit }) {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const {
      currentTab,
      group,
      parentGroup,
      searchId,
      searchName
    } = useGroupSelector();
    const userAccountType = computed(() =>
      store.getters['userAccountType'] !== 'internal' ? 'external' : 'internal'
    );
    const searchType = computed(() => currentTab.value);
    const sortBy = computed(() => `${searchType.value}Name`);
    const groupSelected = computed(() => group.value !== null);
    const parentSelected = computed(() => parentGroup.value !== null);
    const noDataText = computed(() => {
      if (searchType.value === 'subGroup' && !groupSelected.value) {
        return 'You must select a Group first';
      } else {
        return 'No results found';
      }
    });
    const dateFormatter = (date, format = 'MM/DD/YYYY') => {
      return formatDate(date, format);
    };
    const filteredItems = computed(() => {
      // console.log(`filter`, searchName.value, searchId.value);
      let rawItems = store.getters[`groupSelector/${searchType.value}s`];
      let filtered = rawItems;
      if (searchName.value !== '') {
        // console.log(`filter on name`, searchName.value);
        // for subgroups, search both name and id fields
        if (searchType.value === 'subGroup') {
          filtered = rawItems.filter(item => {
            return (
              item[`${searchType.value}Id`].indexOf(searchName.value) !== -1 ||
              item[`${searchType.value}Name`]
                .toLowerCase()
                .indexOf(searchName.value.toLowerCase()) !== -1
            );
          });
        } else {
          filtered = rawItems.filter(item => {
            return (
              item[`${searchType.value}Name`]
                .toLowerCase()
                .indexOf(searchName.value.toLowerCase()) !== -1
            );
          });
        }
      }
      if (searchId.value !== '') {
        // console.log(`filter on eye dee`, searchId.value);
        filtered = rawItems.filter(item => {
          return item[`${searchType.value}Id`].indexOf(searchId.value) !== -1;
        });
      }
      return filtered;
    });
    const isInternalUser = computed(() => store.getters['isInternalUser']);
    const items = computed(() => {
      // console.log('items', isInternalUser.value, searchType.value);
      if (isInternalUser.value === true) {
        if (searchType.value === 'subGroup') {
          return filteredItems.value;
        }
        return store.getters[`groupSelector/${searchType.value}s`];
      } else {
        return filteredItems.value;
      }
    });
    const title = ref('Title here');

    const onItemSelect = item => {
      emit('item-select', item);
    };

    const onViewDetails = item => {
      console.log('ITEM', item);

      store.commit('groupSelector/CLEAR_SHOW_SEARCH');
      let routeData = '';
      switch (searchType.value) {
        case 'group':
          routeData = { name: 'group' };
          store.commit('groupSelector/SET_GROUP_DETAIL_ID', item.groupId);
          break;
        case 'subGroup':
          routeData = { name: 'subgroup' };
          console.log('case subGroup');

          store.commit(
            'groupSelector/SET_SUB_GROUP_DETAIL_CK',
            item.subGroupCk
          );
          if (item.groupCk) {
            store.commit('groupSelector/SET_GROUP_DETAIL_CK', item.groupCk);
            // routeData = {
            //   name: 'subgroup',
            //   params: { groupCk: item.groupCk, subGroupCk: item.subGroupCk }
            // };
          } else {
            store.commit(
              'groupSelector/SET_GROUP_DETAIL_CK',
              group.value.groupCk
            );
            // let groupCk = group.value.groupCk;
            // routeData = {
            //   name: 'subgroup',
            //   params: { groupCk: groupCk, subGroupCk: item.subGroupCk }
            // };
          }
          break;
        case 'parentGroup':
          // TODO:  figure out what this is gonna be.
          routeData = ``;
          break;
      }
      if (routeData !== '') {
        if (route.name !== routeData.name) {
          router.push(routeData).catch(err => {
            if (!(err.name !== 'NavigationDuplicated')) {
              throw err;
            }
          });
        }
      }
    };

    const headers = computed(() => {
      return (
        groupSelectorHeaders[userAccountType.value][searchType.value] || []
      );
    });

    const visibleHeaders = computed(() =>
      headers.value.filter(h => h.visible !== false)
    );

    // onBeforeMount(() => {
    //   console.log('searchName', searchName.value);
    // });

    return {
      dateFormatter,
      headers,
      isFunction,
      items,
      noDataText,
      onItemSelect,
      onViewDetails,
      parentSelected,
      sortBy,
      title,
      visibleHeaders
    };
  },
  emits: ['item-selected'],
  components: {
    AdvancedTable,
    TruncateConcat
  },
  methods: {
    getCellValue(item, header) {
      // for some reason, vuetify's data table can't find this
      //   function if it is defined in setup.  ¯\_(ツ)_/¯
      // console.log(`getCellValue`, header.value);
      if (isFunction(header.value)) {
        // console.log(`gcv: ${header.value(item)}`);
        if (!header.value(item)) {
          console.warn(`value not found.  check your headers`, item, header);
        }
        return header.value(item);
      } else {
        // console.log(`gcv2: ${header.value}::${item[header.value]}`);
        if (!item[header.value]) {
          console.warn(`value not found.  check your headers`, item, header);
        }
        return item[header.value];
      }
    }
  },
  watch: {
    $route(oRoute, nRoute) {
      console.log('ROUTE WATCH', oRoute, nRoute);
    }
  }
};
</script>

<style lang="scss" scoped>
.search-results {
  background-color: $white;
  color: $black;
  margin-top: 0.5rem;
  width: 100%;

  #gs-results {
    padding: 0;
  }

  ul.v-pagination.theme--light {
    min-width: 50%;
  }
  .select {
    color: $leaf;
    text-decoration: underline;
  }
}
</style>
